import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { RockBTSSpecimen } from '../../../../types/rockBts';
import type { BtsSpecimenPreview } from '../computations';
import { average } from '../computations';
// Shared section idiom (reused read-only from the Rock TCS module)
import { fmt, paperSx, SectionHeader } from '../../rock-tcs/sectionStyles';

interface Props {
  specimens: RockBTSSpecimen[];
  previews: BtsSpecimenPreview[];
  specimenCount: number;
}

const cellSx = { py: 0.5, px: 1, fontSize: '0.74rem' } as const;
const headSx = { ...cellSx, fontWeight: 700, color: 'text.secondary' } as const;

/**
 * Result Entry — Summary of Results panel (mockup bottom strip). Read-only,
 * UI-only: per-specimen reported values plus cross-specimen averages
 * (averages are NOT printed on the report).
 */
export const SummaryOfResults = ({ specimens, previews, specimenCount }: Props) => {
  const included = specimens
    .map((s, i) => ({ s, p: previews[i] }))
    .filter(({ s }) => s.specimenNumber <= specimenCount);

  const avgTensile = average(included.map(({ p }) => p.tensileStrengthMPa));
  const avgMaxLoad = average(included.map(({ s }) => s.maxLoadKN));

  return (
    <Paper variant="outlined" sx={paperSx}>
      <SectionHeader label="Summary of Results" />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>Specimen</TableCell>
            <TableCell sx={headSx} align="right">Tensile Strength (MPa)</TableCell>
            <TableCell sx={headSx} align="right">Max Load at Failure (kN)</TableCell>
            <TableCell sx={headSx} align="right">Loading Rate (kN/s)</TableCell>
            <TableCell sx={headSx} align="right">Loading Rate (MPa/s)</TableCell>
            <TableCell sx={headSx}>Fracture Mode</TableCell>
            <TableCell sx={headSx}>Plane Type</TableCell>
            <TableCell sx={headSx}>Failure Type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {included.map(({ s, p }) => (
            <TableRow key={s.specimenNumber}>
              <TableCell sx={cellSx}>Specimen {s.specimenNumber}</TableCell>
              <TableCell sx={cellSx} align="right">{fmt(p.tensileStrengthMPa, 1)}</TableCell>
              <TableCell sx={cellSx} align="right">{fmt(s.maxLoadKN, 1)}</TableCell>
              <TableCell sx={cellSx} align="right">{fmt(s.loadingRateKNs, 3)}</TableCell>
              <TableCell sx={cellSx} align="right">{fmt(p.loadingRateMPas, 2)}</TableCell>
              <TableCell sx={cellSx}>{s.primaryFractureMode ?? '—'}</TableCell>
              <TableCell sx={cellSx}>{s.primaryPlaneType ?? '—'}</TableCell>
              <TableCell sx={cellSx}>{s.failureType ?? '—'}</TableCell>
            </TableRow>
          ))}
          {specimenCount > 1 && (
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Average (UI only)</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }} align="right">{fmt(avgTensile, 1)}</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }} align="right">{fmt(avgMaxLoad, 1)}</TableCell>
              <TableCell sx={cellSx} colSpan={5} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.64rem', display: 'block', mt: 0.5 }}>
        Averages are operator guidance only — the report prints per-specimen values.
      </Typography>
    </Paper>
  );
};
